import { Controller } from '@hotwired/stimulus'
import { JsonTreeNode as TreeNode } from '../modules/checkbox_tree'

export default class extends Controller {
  static targets = [
    'inapplicableList', 'applicableList', 'groupTemplate', 'disabledType',
    'applicabilityType', 'facilityIds', 'organizationIds', 'submit'
  ]

  static values = {
    canUpdateGlobal: Boolean
  }

  declare groupTemplateTarget: HTMLTemplateElement
  declare facilityIdsTarget: HTMLInputElement
  declare organizationIdsTarget: HTMLInputElement
  declare applicabilityTypeTarget: HTMLInputElement
  declare disabledTypeTarget: HTMLInputElement
  declare inapplicableListTarget: HTMLDivElement
  declare applicableListTarget: HTMLDivElement
  declare submitTarget: HTMLButtonElement
  declare canUpdateGlobalValue: boolean

  checkedList: TreeNode[] = []
  uncheckedList: TreeNode[] = []

  private initialCheckedList: TreeNode[] = []
  private initialUncheckedList: TreeNode[] = []
  private initialized = false
  private readonly boundHandleTreeChange = this.handleTreeChange.bind(this)
  private readonly boundHandleSubmit = this.handleSubmit.bind(this)

  connect (): void {
    document.addEventListener('checkbox-tree:changed', this.boundHandleTreeChange)
    this.element.addEventListener('submit', this.boundHandleSubmit)
  }

  disconnect (): void {
    document.removeEventListener('checkbox-tree:changed', this.boundHandleTreeChange)
    this.element.removeEventListener('submit', this.boundHandleSubmit)
  }

  private handleTreeChange (event: Event): void {
    const { checkedTree, uncheckedTree } = (event as CustomEvent).detail

    this.checkedList = checkedTree
    this.uncheckedList = uncheckedTree

    if (!this.initialized) {
      this.initialCheckedList = structuredClone(checkedTree)
      this.initialUncheckedList = structuredClone(uncheckedTree)
      this.initialized = true
    }

    this.renderLists()
    this.togglePlaceholders()
    this.toggleSubmitButton()
    this.updateFormFields()
    this.updateValues()
  }

  private handleSubmit (): void {
    if (this.disabledTypeTarget.value === 'groups') {
      this.updateValues()
    } else {
      this.organizationIdsTarget.value = ''
      this.facilityIdsTarget.value = ''
    }
  }

  private updateFacilityIds (treeData: TreeNode[]): TreeNode[] {
    return treeData.map(group => ({
      ...group,
      children: group.fullyUnchecked
        ? []
        : group.children.filter(child => !child.checked)
    }))
  }

  private updateFormFields (): void {
    const hasChecked = this.checkedList.length > 0
    const hasUnchecked = this.uncheckedList.length > 0

    this.disabledTypeTarget.value = this.canUpdateGlobalValue
      ? (hasUnchecked && hasChecked ? 'groups' : 'global')
      : 'groups'

    this.applicabilityTypeTarget.value = hasUnchecked ? 'not_applicable' : 'applicable'
  }

  private renderLists (): void {
    this.renderDiff(this.uncheckedList, this.initialUncheckedList, this.inapplicableListTarget)
    this.renderDiff(this.checkedList, this.initialCheckedList, this.applicableListTarget)
  }

  private renderDiff (current: TreeNode[], initial: TreeNode[], container: HTMLElement): void {
    container.innerHTML = ''
    const diff = this.getMovedToList(current, initial)
    this.renderList(diff, container)
  }

  private getMovedToList (currentList: TreeNode[], initialList: TreeNode[]): TreeNode[] {
    const initialIds = new Set<string>()
    this.addIdsToSet(initialList, initialIds)

    return currentList.reduce<TreeNode[]>((moved, group) => {
      const wasGroupInInitial = initialIds.has(group.id)
      const movedChildren = group.children.filter(child => !initialIds.has(child.id))

      if (!wasGroupInInitial && group.children.length > 0) {
        moved.push({ ...group })
      } else if (movedChildren.length > 0) {
        moved.push({ ...group, children: movedChildren })
      }

      return moved
    }, [])
  }

  private addIdsToSet (treeNodes: TreeNode[], idSet: Set<string>): void {
    treeNodes.forEach(({ id, children }) => {
      idSet.add(id)
      children.forEach(child => idSet.add(child.id))
    })
  }

  private renderList (data: TreeNode[], container: HTMLElement): void {
    data.forEach(group => {
      const groupNode = this.groupTemplateTarget.content.cloneNode(true) as HTMLElement
      const groupName = groupNode.querySelector('#group-name')
      const entityNamesContainer = groupNode.querySelector('#entity-names')

      if (!(groupName instanceof HTMLSpanElement) || !(entityNamesContainer instanceof HTMLDivElement)) return

      groupName.textContent = group.name
      entityNamesContainer.innerHTML = ''

      group.children.forEach(entity => {
        entityNamesContainer.appendChild(this.renderEntityTag(entity.name))
      })

      container.appendChild(groupNode)
    })
  }

  private renderEntityTag (name: string): HTMLElement {
    const span = document.createElement('span')
    span.className = 'lt-badge lt-badge-outline'
    span.textContent = name
    return span
  }

  private updateValues (): void {
    const isApplicable = this.applicabilityTypeTarget.value === 'applicable'
    let organizationIds: number[] = []
    let facilityIds: number[] = []

    if (isApplicable) {
      ({ organizationIds, facilityIds } = this.extractIdsFromTree(this.checkedList, true))
    } else {
      const filteredUncheckedList = this.updateFacilityIds(this.uncheckedList) as unknown as TreeNode[]
      ({ organizationIds, facilityIds } = this.extractIdsFromTree(filteredUncheckedList, false))
    }

    this.organizationIdsTarget.value = organizationIds.join(',')
    this.facilityIdsTarget.value = facilityIds.join(',')
  }

  private extractIdsFromTree (treeData: TreeNode[], isApplicable: boolean): { organizationIds: number[], facilityIds: number[] } {
    const organizationIds: number[] = []
    const facilityIds: number[] = []

    treeData.forEach(group => {
      if (isApplicable ? group.fullyChecked : group.fullyUnchecked) {
        organizationIds.push(Number(group.id))
      } else {
        group.children.forEach(child => {
          if (isApplicable ? child.checked : !child.checked) {
            facilityIds.push(Number(child.id))
          }
        })
      }
    })

    return { organizationIds, facilityIds }
  }

  private isChanged (): boolean {
    return JSON.stringify(this.checkedList) !== JSON.stringify(this.initialCheckedList) ||
      JSON.stringify(this.uncheckedList) !== JSON.stringify(this.initialUncheckedList)
  }

  private toggleSubmitButton (): void {
    this.submitTarget.disabled = !this.isChanged()
  }

  private togglePlaceholders (): void {
    const noInapplicabilityChanges = document.getElementById('no-inapplicability-changes')
    const noApplicabilityChanges = document.getElementById('no-applicability-changes')

    this.togglePlaceholder(noInapplicabilityChanges, this.inapplicableListTarget.children.length === 0)
    this.togglePlaceholder(noApplicabilityChanges, this.applicableListTarget.children.length === 0)
  }

  private togglePlaceholder (container: HTMLElement | null, isEmpty: boolean): void {
    if (container != null) {
      container.classList.toggle('hidden', !isEmpty)
    }
  }
}
